import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

export const ContactPage = () => {
  const location = useLocation();

  // Scroll to quote form if hash is present
  useEffect(() => {
    if (location.hash === '#quote-form') {
      setTimeout(() => {
        const element = document.getElementById('quote-form');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section 
        data-testid="contact-hero"
        className="bg-steel-dark py-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-body text-primary font-medium tracking-wide mb-4 animate-fade-in">
            Get in Touch
          </p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-4 animate-slide-in-bottom">
            Contact Us
          </h1>
          <p className="font-body text-slate-400 text-base sm:text-lg max-w-2xl animate-fade-in" style={{ animationDelay: '100ms' }}>
            Ready to discuss your project? We're here to help bring your steel vision to life.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section 
        data-testid="contact-content"
        className="py-24 bg-background"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map */}
            <div 
              data-testid="contact-map-container"
              className="h-[400px] lg:h-full min-h-[400px] border border-slate-200"
            >
              <iframe
                data-testid="contact-map"
                title="NJ Steel Fabrications Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3157.4908068193837!2d144.94899431531645!3d-37.66682497977723!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6ad65a0000000001%3A0x0!2sSomerton%20Park%20Dr%2C%20Campbellfield%20VIC%203061!5e0!3m2!1sen!2sau!4v1647904321000!5m2!1sen!2sau"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="font-heading font-extrabold text-3xl text-steel-dark uppercase tracking-tight mb-8">
                Contact Information
              </h2>

              <div className="space-y-8">
                {/* Address */}
                <div 
                  data-testid="contact-address"
                  className="flex items-start gap-5"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-primary/10 flex-shrink-0">
                    <MapPin className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-steel-dark uppercase tracking-tight mb-2">
                      Address
                    </h3>
                    <p className="font-body text-steel-gray text-base leading-relaxed">
                      5/17-19 Somerton Park Drive,<br />
                      Campbellfield VIC 3061<br />
                      Victoria, Australia
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div 
                  data-testid="contact-email"
                  className="flex items-start gap-5"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-primary/10 flex-shrink-0">
                    <Mail className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-steel-dark uppercase tracking-tight mb-2">
                      E-mail
                    </h3>
                    <a 
                      href="mailto:njsteelfab@gmail.com"
                      data-testid="contact-email-link"
                      className="font-body text-steel-gray text-base hover:text-primary transition-colors"
                    >
                      njsteelfab@gmail.com
                    </a>
                  </div>
                </div>

                {/* Telephone */}
                <div 
                  data-testid="contact-phone"
                  className="flex items-start gap-5"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-primary/10 flex-shrink-0">
                    <Phone className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-steel-dark uppercase tracking-tight mb-2">
                      Telephone
                    </h3>
                    <a 
                      href="tel:0424456596"
                      data-testid="contact-phone-link"
                      className="font-body text-steel-gray text-base hover:text-primary transition-colors"
                    >
                      0424 456 596
                    </a>
                  </div>
                </div>

                {/* Business Hours */}
                <div 
                  data-testid="contact-hours"
                  className="flex items-start gap-5"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-primary/10 flex-shrink-0">
                    <Clock className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-steel-dark uppercase tracking-tight mb-2">
                      Business Hours
                    </h3>
                    <p className="font-body text-steel-gray text-base">
                      Monday - Friday: 7:00 AM - 5:00 PM<br />
                      Saturday: By Appointment<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Service Area */}
              <div 
                data-testid="service-area"
                className="mt-12 p-8 bg-white border border-slate-200"
              >
                <h3 className="font-heading font-bold text-xl text-steel-dark uppercase tracking-tight mb-4">
                  Service Area
                </h3>
                <p className="font-body text-steel-gray text-base leading-relaxed">
                  We proudly serve Melbourne and parts of Victoria, providing comprehensive steel fabrication services for residential, commercial, and industrial projects. Our crane truck services extend across urban, suburban, and rural areas with flexible scheduling options.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section 
        data-testid="quick-contact"
        className="py-12 bg-primary"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                Ready to Start Your Project?
              </h3>
              <p className="font-body text-white/80 text-base mt-1">
                Call us today for a free consultation and quote
              </p>
            </div>
            <a 
              href="tel:0424456596"
              data-testid="quick-call-button"
              className="flex items-center gap-3 bg-white px-8 py-4 font-heading font-bold text-primary uppercase tracking-widest hover:bg-slate-100 transition-colors"
            >
              <Phone className="w-5 h-5" />
              0424 456 596
            </a>
          </div>
        </div>
      </section>

      {/* Google Form Section */}
      <section 
        id="quote-form"
        data-testid="quote-form-section"
        className="py-24 bg-background"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-body text-primary font-medium tracking-wide mb-4">
              Request a Quote
            </p>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-steel-dark uppercase tracking-tight mb-4">
              Send Us Your Project Details
            </h2>
            <p className="font-body text-steel-gray text-base max-w-2xl mx-auto">
              Fill out the form below and our team will get back to you with a detailed quote for your steel fabrication project.
            </p>
          </div>
          
          <div 
            data-testid="google-form-container"
            className="bg-white border border-slate-200 p-4"
          >
            <iframe
              data-testid="google-form"
              src="https://docs.google.com/forms/d/e/1FAIpQLSe07nLm6e_pp9YeWMYTg2FyR6zeuEeYKBS-yvOZ9to_1VLt7Q/viewform?embedded=true"
              width="100%"
              height="800"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              title="Quote Request Form"
              className="w-full"
            >
              Loading…
            </iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
